import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '../../../i18n/useI18n';
import { toCalendarDay } from '../../../utils/dateTimeUtils';
import { LiksButton } from '../LiksButton/LiksButton';
import { LiksDialog } from '../LiksDialog/LiksDialog';
import { LiksIconButton } from '../LiksIconButton/LiksIconButton';
import {
  formatLiksDateDayLabel,
  resolveLiksDateRangePreset,
  shiftCalendarMonth,
  type LiksDateDayRange,
  type LiksDateRangePreset,
} from './dateRangePicker';
import { LiksDateRangeCalendarGrid } from './LiksDateRangeCalendarGrid';
import './LiksDateRangeDialog.css';

const PRESETS: Exclude<LiksDateRangePreset, 'entireTime'>[] = [
  'today',
  'thisWeek',
  'thisMonth',
  'last7Days',
  'last30Days',
];

function resolveVisibleMonth(
  fromDay: string,
  toDay: string,
): { year: number; month: number } {
  const anchorDay = toDay || fromDay;
  if (anchorDay) {
    const [year, month] = anchorDay.split('-').map(Number);
    return { year, month };
  }
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  };
}

export type LiksDateRangeDialogContentProps = {
  fromDay: string;
  toDay: string;
  onClose: () => void;
  onApply: (range: LiksDateDayRange | null) => void;
};

export function LiksDateRangeDialogContent({
  fromDay,
  toDay,
  onClose,
  onApply,
}: LiksDateRangeDialogContentProps) {
  const { locale, translate } = useI18n();
  const today = toCalendarDay(new Date());
  const [draftFromDay, setDraftFromDay] = useState(fromDay);
  const [draftToDay, setDraftToDay] = useState(toDay);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    resolveVisibleMonth(fromDay, toDay),
  );

  const isFromAfterToday = draftFromDay !== '' && draftFromDay > today;
  const isInvalidOrder =
    draftFromDay !== '' && draftToDay !== '' && draftToDay < draftFromDay;
  const canApply =
    (draftFromDay === '' && draftToDay === '') ||
    (draftFromDay !== '' &&
      draftToDay !== '' &&
      !isInvalidOrder &&
      !isFromAfterToday);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
      }).format(new Date(visibleMonth.year, visibleMonth.month - 1, 1)),
    [locale, visibleMonth.month, visibleMonth.year],
  );

  const weekdayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // Monday-first: 2024-01-01 was Monday
    return Array.from({ length: 7 }, (_, index) =>
      formatter.format(new Date(2024, 0, 1 + index)),
    );
  }, [locale]);

  const [todayYear, todayMonth] = today.split('-').map(Number);
  const canGoNextMonth =
    visibleMonth.year < todayYear ||
    (visibleMonth.year === todayYear && visibleMonth.month < todayMonth);

  const handlePreset = (preset: LiksDateRangePreset) => {
    const range = resolveLiksDateRangePreset(preset);
    if (range == null) {
      setDraftFromDay('');
      setDraftToDay('');
      return;
    }
    setDraftFromDay(range.fromDay);
    setDraftToDay(range.toDay);
    const [year, month] = range.toDay.split('-').map(Number);
    setVisibleMonth({ year, month });
  };

  const handleDayClick = (day: string) => {
    if (day > today) {
      return;
    }

    if (draftFromDay === '' || (draftFromDay !== '' && draftToDay !== '')) {
      setDraftFromDay(day);
      setDraftToDay('');
      return;
    }

    if (day < draftFromDay) {
      setDraftToDay(draftFromDay);
      setDraftFromDay(day);
      return;
    }

    setDraftToDay(day);
  };

  const handleClear = () => {
    onApply(null);
  };

  const handleApply = () => {
    if (!canApply) {
      return;
    }
    if (draftFromDay === '' || draftToDay === '') {
      onApply(null);
      return;
    }
    onApply({ fromDay: draftFromDay, toDay: draftToDay });
  };

  return (
    <LiksDialog
      isOpen
      size="sm"
      title={translate('common.dateRange.title')}
      closeLabel={translate('common.dateRange.close')}
      onClose={onClose}
      footerClassName="liks-date-range-dialog__footer"
      footer={
        <>
          <LiksButton variant="secondary" onClick={handleClear}>
            {translate('common.dateRange.clear')}
          </LiksButton>
          <div className="liks-date-range-dialog__actions-end">
            <LiksButton variant="secondary" onClick={onClose}>
              {translate('common.dateRange.cancel')}
            </LiksButton>
            <LiksButton onClick={handleApply} disabled={!canApply}>
              {translate('common.dateRange.apply')}
            </LiksButton>
          </div>
        </>
      }
    >
      <div className="liks-date-range-dialog__presets">
        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className="liks-date-range-dialog__preset"
            onClick={() => handlePreset(preset)}
          >
            {translate(`common.dateRange.presets.${preset}`)}
          </button>
        ))}
        <button
          type="button"
          className="liks-date-range-dialog__preset"
          onClick={() => handlePreset('entireTime')}
        >
          {translate('common.dateRange.entireTime')}
        </button>
      </div>

      <div className="liks-date-range-dialog__month-nav">
        <LiksIconButton
          variant="outlined"
          aria-label={translate('common.dateRange.prevMonth')}
          onClick={() =>
            setVisibleMonth((current) =>
              shiftCalendarMonth(current.year, current.month, -1),
            )
          }
        >
          <ChevronLeft strokeWidth={2} aria-hidden />
        </LiksIconButton>
        <span className="liks-date-range-dialog__month-label">
          {monthLabel}
        </span>
        <LiksIconButton
          variant="outlined"
          aria-label={translate('common.dateRange.nextMonth')}
          disabled={!canGoNextMonth}
          onClick={() =>
            setVisibleMonth((current) =>
              shiftCalendarMonth(current.year, current.month, 1),
            )
          }
        >
          <ChevronRight strokeWidth={2} aria-hidden />
        </LiksIconButton>
      </div>

      <LiksDateRangeCalendarGrid
        year={visibleMonth.year}
        month={visibleMonth.month}
        monthLabel={monthLabel}
        weekdayLabels={weekdayLabels}
        today={today}
        draftFromDay={draftFromDay}
        draftToDay={draftToDay}
        onDayClick={handleDayClick}
      />

      <div className="liks-date-range-dialog__selection">
        <div>
          <span className="liks-date-range-dialog__selection-label">
            {translate('common.dateRange.from')}
          </span>
          <span className="liks-date-range-dialog__selection-value">
            {draftFromDay
              ? formatLiksDateDayLabel(draftFromDay, locale)
              : translate('common.dateRange.notSet')}
          </span>
        </div>
        <div>
          <span className="liks-date-range-dialog__selection-label">
            {translate('common.dateRange.to')}
          </span>
          <span className="liks-date-range-dialog__selection-value">
            {draftToDay
              ? formatLiksDateDayLabel(draftToDay, locale)
              : translate('common.dateRange.notSet')}
          </span>
        </div>
      </div>

      {isFromAfterToday ? (
        <p className="liks-date-range-dialog__error" role="alert">
          {translate('common.dateRange.fromAfterToday')}
        </p>
      ) : isInvalidOrder ? (
        <p className="liks-date-range-dialog__error" role="alert">
          {translate('common.dateRange.invalid')}
        </p>
      ) : null}
    </LiksDialog>
  );
}
